import React, { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  Droplet,
  Pill,
  Activity,
  FlaskConical,
  Stethoscope,
  Brain,
  ListChecks,
  Scale,
  Baby,
  Siren,
  Beaker,
  Flame,
  MessageCircleHeart,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageProvider";
import TopRightWidget from "@/components/TopRightWidget";
import VersionBadge from "@/components/VersionBadge";
import GeminiButton from "@/components/GeminiButton";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageToggle from "@/components/LanguageToggle";

// New v2.0 structure:
//   1. Emergency & Critical Care: Emergency Drugs, Heparin, IV Fluid, IV Drip Rate, Electrolytes
//   2. Scores & Specialty: NIHSS, Alvarado, OB-Gyn
//   3. Others / Lainnya: Pediatric Dosing, BMI
const NAV = [
  { kind: "group", labelKey: "nav.group.critical" },
  { kind: "item", to: "/emergency-drugs", labelKey: "nav.emergency", subKey: "nav.emergency.sub", icon: Siren, testid: "nav-emergency" },
  { kind: "item", to: "/heparin", labelKey: "nav.heparin", subKey: "nav.heparin.sub", icon: Beaker, testid: "nav-heparin" },
  { kind: "item", to: "/baxter", labelKey: "nav.baxter", subKey: "nav.baxter.sub", icon: Flame, testid: "nav-baxter" },
  { kind: "item", to: "/iv-fluid", labelKey: "nav.iv_fluid", subKey: "nav.iv_fluid.sub", icon: Droplet, testid: "nav-iv-fluid" },
  { kind: "item", to: "/drip-rate", labelKey: "nav.drip", subKey: "nav.drip.sub", icon: Activity, testid: "nav-drip-rate" },
  { kind: "item", to: "/electrolytes", labelKey: "nav.electrolytes", subKey: "nav.electrolytes.sub", icon: FlaskConical, testid: "nav-electrolytes" },

  { kind: "group", labelKey: "nav.group.scores" },
  { kind: "item", to: "/nihss", labelKey: "nav.nihss", subKey: "nav.nihss.sub", icon: Brain, testid: "nav-nihss" },
  { kind: "item", to: "/alvarado", labelKey: "nav.alvarado", subKey: "nav.alvarado.sub", icon: ListChecks, testid: "nav-alvarado" },
  { kind: "item", to: "/killip", labelKey: "score.killip.title", subKey: "score.killip.desc", icon: Siren, testid: "nav-killip" },
  { kind: "item", to: "/sofa", labelKey: "score.sofa.title", subKey: "score.sofa.desc", icon: Activity, testid: "nav-sofa" },
  { kind: "item", to: "/news", labelKey: "score.news.title", subKey: "score.news.desc", icon: Activity, testid: "nav-news" },
  { kind: "item", to: "/obgyn", labelKey: "nav.obgyn", subKey: "nav.obgyn.sub", icon: Baby, testid: "nav-obgyn" },

  { kind: "group", labelKey: "nav.group.neonatology" },
  { kind: "item", to: "/apgar", labelKey: "score.apgar.title", subKey: "score.apgar.desc", icon: Baby, testid: "nav-apgar" },
  { kind: "item", to: "/ballard", labelKey: "score.ballard.title", subKey: "score.ballard.desc", icon: Baby, testid: "nav-ballard" },
  { kind: "item", to: "/downe", labelKey: "score.downe.title", subKey: "score.downe.desc", icon: Baby, testid: "nav-downe" },

  { kind: "group", labelKey: "nav.group.others" },
  { kind: "item", to: "/pediatric-dose", labelKey: "nav.pediatric", subKey: "nav.pediatric.sub", icon: Pill, testid: "nav-pediatric-dose" },
  { kind: "item", to: "/bmi", labelKey: "nav.bmi", subKey: "nav.bmi.sub", icon: Scale, testid: "nav-bmi" },
];

function LanguageSwitcher() {
  const { t } = useLanguage();
  return (
    <div className="px-4 md:px-6 py-3 border-b border-stone-200 w-full" data-testid="language-switcher">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] tracking-[0.14em] uppercase font-semibold text-stone-500">
          {t("sidebar.lang_label")}
        </span>
        <LanguageToggle />
      </div>
    </div>
  );
}

function ThemeRow() {
  const { t } = useLanguage();
  return (
    <div className="px-4 md:px-6 py-3 border-t border-stone-200 w-full" data-testid="theme-switcher">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[10px] tracking-[0.14em] uppercase font-semibold text-stone-500">
          {t("theme.label")}
        </span>
        <ThemeToggle />
      </div>
    </div>
  );
}

export default function Layout() {
  const { t } = useLanguage();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#F5F5F4] text-stone-900 flex flex-col md:flex-row">
      {/* Controlled feedback dialog — desktop trigger inside TopRightWidget,
          mobile trigger inside the mobile header below. */}
      <TopRightWidget open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <VersionBadge />
      <GeminiButton />

      {/* MOBILE-ONLY 3-column header. Hidden at md and above. */}
      <header
        data-testid="mobile-header"
        className="md:hidden bg-white border-b border-stone-200 px-3 py-2.5 grid items-center gap-2"
        style={{ gridTemplateColumns: "auto 1fr auto" }}
      >
        {/* Left — HMMM logo + title */}
        <Link to="/" data-testid="mobile-brand-link" className="flex items-center gap-2 shrink-0">
          <div className="h-8 w-8 rounded-sm bg-sky-700 text-white flex items-center justify-center">
            <Stethoscope size={16} strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-medium tracking-tight text-sm text-stone-900">HMMM</div>
            <div className="text-[9px] tracking-[0.14em] uppercase text-stone-500 whitespace-nowrap">
              {t("brand.subtitle")}
            </div>
          </div>
        </Link>

        {/* Center — Language + Theme stacked vertically */}
        <div className="flex flex-col items-center gap-1.5" data-testid="mobile-toggles">
          <LanguageToggle />
          <ThemeToggle />
        </div>

        {/* Right — Compact Feedback button (opens the same dialog) */}
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          data-testid="mobile-feedback-btn"
          aria-label={t("widget.label")}
          className="inline-flex items-center justify-center gap-1 bg-sky-700 text-white hover:bg-sky-800 rounded-sm px-2.5 py-2 min-h-[40px] min-w-[40px] text-xs font-medium shadow-sm"
        >
          <MessageCircleHeart size={16} strokeWidth={1.75} />
        </button>
      </header>

      {/* Sidebar — full on desktop, nav-only on mobile (brand/lang/theme
          were moved to the mobile-header above). */}
      <aside
        className="md:w-72 md:min-h-screen md:border-r border-b md:border-b-0 border-stone-200 bg-white md:sticky md:top-0 flex md:flex-col"
        data-testid="app-sidebar"
      >
        <div className="hidden md:flex px-4 md:px-6 py-5 md:py-7 border-b border-stone-200 items-center gap-3 w-full">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="h-9 w-9 rounded-sm bg-sky-700 text-white flex items-center justify-center">
              <Stethoscope size={18} strokeWidth={1.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-medium tracking-tight text-base text-stone-900">HMMM</div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-stone-500">{t("brand.subtitle")}</div>
            </div>
          </Link>
        </div>

        <div className="hidden md:block">
          <LanguageSwitcher />
        </div>

        <nav className="flex md:flex-col overflow-x-auto md:overflow-x-visible md:py-3 w-full" data-testid="sidebar-nav">
          {NAV.map((item, idx) => {
            if (item.kind === "group") {
              return (
                <div
                  key={`group-${idx}`}
                  className="px-4 md:px-6 pt-4 pb-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-sky-700 hidden md:block"
                  data-testid={`nav-group-${idx}`}
                >
                  {t(item.labelKey)}
                </div>
              );
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  cn(
                    "group flex items-start gap-3 px-4 md:px-6 py-3 md:py-2.5 min-h-[48px] md:min-h-[44px] border-l-2 transition-colors duration-100 whitespace-nowrap md:whitespace-normal",
                    isActive
                      ? "border-sky-700 bg-stone-100 text-stone-900"
                      : "border-transparent text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                  )
                }
              >
                <Icon size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                <div className="leading-tight">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {t(item.labelKey)}
                    {item.inDev && (
                      <span
                        data-testid={`${item.testid}-dev-badge`}
                        className="font-mono text-[9px] tracking-[0.12em] uppercase font-semibold bg-amber-700/10 text-amber-900 border border-amber-700/30 px-1.5 py-0.5 rounded-sm"
                      >
                        {t("common.dev_badge")}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] tracking-wide text-stone-500 hidden md:block">
                    {item.inDev ? t("nav.dev_prefix") : ""}
                    {t(item.subKey)}
                  </div>
                </div>
              </NavLink>
            );
          })}
        </nav>

        <div className="md:mt-auto w-full hidden md:block">
          <ThemeRow />
          <div className="hidden md:block px-4 md:px-6 py-5 border-t border-stone-200 text-[11px] leading-relaxed text-stone-500">
            <p>{t("sidebar.footer.disclaimer")}</p>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <div className="flex-1">
          <Outlet />
        </div>
        <footer
          data-testid="privacy-footer"
          className="mt-auto border-t border-stone-200 bg-white px-4 md:px-10 py-6 text-center"
        >
          <p className="font-display text-sm md:text-base font-medium text-stone-800 tracking-tight">
            {t("footer.tagline")}
          </p>
          <p className="mt-2 text-[11px] md:text-xs text-stone-600 leading-relaxed max-w-2xl mx-auto">
            {t("footer.privacy")}
          </p>
        </footer>
      </main>
    </div>
  );
}
